import { t, useStoreLanguage } from "../../../features/store-language/i18n.ts";
import { useEffect, useRef, useState } from "react";
import { Button, Form, Input, Tooltip } from "antd";
import { DisconnectOutlined, GoogleOutlined, LockOutlined, ReloadOutlined, UserOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import AppFeedback from "../../shared/AppFeedback.tsx";
import LoadingRing from "../../shared/LoadingRing.jsx";
import CustomerAuthShell from "./CustomerAuthShell.tsx";
import { getIdentityContext, loginIdentity, type IdentityContext } from "./customerIdentityService.ts";
import "./CustomerLogin.css";

// The dedicated administration URL resolves the host; only platform credentials are submitted here.
export default function CustomerLogin() {
  const storeLanguage = useStoreLanguage();
  const navigate = useNavigate();
  const [context, setContext] = useState<IdentityContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [contextError, setContextError] = useState(false);
  const [pending, setPending] = useState(false);
  const inFlight = useRef(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(function () {
    let active = true;
    setLoading(true);
    getIdentityContext().then(function (resolved) {
      if (!active) return;
      if (resolved.accountKind !== "platform") {
        navigate("/login", { replace: true });
        return;
      }
      if (resolved.isAuthenticated) {
        navigate("/admin/", { replace: true });
        return;
      }
      setContext(resolved);
      setContextError(false);
    }).catch(function () {
      if (!active) return;
      setContext(null);
      setContextError(true);
    }).finally(function () {
      if (active) setLoading(false);
    });
    return function () { active = false; };
  }, [storeLanguage, navigate, attempt]);

  async function submit(values: { userName: string; password: string }) {
    if (!context || inFlight.current) return;
    inFlight.current = true;
    setPending(true);
    try {
      const result = await loginIdentity(values.userName, values.password, context.csrfToken);
      if (result.accountKind !== "platform") throw new Error(t("پاسخ ورود معتبر نیست."));
      localStorage.removeItem("jwt");
      localStorage.removeItem("user");
      localStorage.removeItem("actionTypes");
      navigate("/admin/", { replace: true });
    } catch (error) {
      AppFeedback.httpError(error);
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  }

  return (
    <CustomerAuthShell
      companyInfo={{ name: t("رَج") }}
      title={contextError ? t("بررسی وضعیت ورود انجام نشد") : t("ورود به مدیریت مرکزی پورتال نمایندگان")}
      description={contextError ? t("در حال حاضر نمی‌توانیم صفحهٔ ورود را آماده کنیم. برای ادامه دوباره تلاش کنید.") : ""}
      variant={contextError ? "login-status" : "login"}
      eyebrow={contextError ? t("ورود به مدیریت مرکزی") : undefined}
      statusIcon={contextError ? <DisconnectOutlined /> : undefined}
    >
      {contextError ? (
        <div
          className="customer-login-status"
          role="alert"
        >
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={function () {
              setLoading(true);
              setAttempt(function (value) { return value + 1; });
            }}
            disabled={loading}
            loading={loading}
            block
          >
            {t("تلاش دوباره برای بررسی ورود")}
          </Button>
          <p>{t("اگر مشکل ادامه داشت، کمی بعد دوباره مراجعه کنید.")}</p>
        </div>
      ) : loading ? <LoadingRing tip={t("در حال بررسی…")} /> : !context ? null : (
        <>
          <div
            className="customer-auth-mode"
            aria-label={t("روش ورود")}
          >
            <button
              type="button"
              className="is-active"
              aria-pressed={true}
              disabled={pending}
            >
              <LockOutlined />
              {t("رمز عبور")}
            </button>
            <Tooltip title={t("ورود پیامکی برای مدیران در دسترس نیست.")}>
              <button
                type="button"
                aria-pressed={false}
                disabled
              >
                {t("کد پیامکی")}
              </button>
            </Tooltip>
          </div>

          <Form
            layout="vertical"
            requiredMark={false}
            onFinish={submit}
            onFinishFailed={function () {
              AppFeedback.error({ title: t("اطلاعات ورود را کامل کنید.") });
            }}
            disabled={pending}
            className="customer-auth-form"
          >
            <Form.Item
              name="userName"
              label={t("نام کاربری")}
              rules={[{ required: true, whitespace: true, message: t("نام کاربری را وارد کنید.") }]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder={t("نام کاربری را وارد کنید.")}
                autoComplete="username"
                dir="ltr"
              />
            </Form.Item>
            <Form.Item
              name="password"
              label={t("رمز عبور")}
              rules={[{ required: true, message: t("رمز عبور را وارد کنید.") }]}
            >
              <Input.Password
                placeholder={t("رمز عبور خود را وارد کنید")}
                autoComplete="current-password"
              />
            </Form.Item>

            <div className="customer-auth-form-link">
              <Tooltip title={t("بازیابی رمز عبور مدیران در دسترس نیست.")}>
                <Button
                  type="link"
                  disabled
                >
                  {t("رمز عبور را فراموش کرده‌اید؟")}
                </Button>
              </Tooltip>
            </div>

            <div className="customer-auth-actions-group">
              <Form.Item className="customer-auth-submit">
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  loading={pending}
                  disabled={pending}
                >
                  {t("ورود به مدیریت مرکزی")}
                </Button>
              </Form.Item>
              <div
                className="customer-auth-alternatives"
                aria-label={t("روش‌های دیگر ورود")}
              >
                <div className="customer-auth-divider">
                  <span>{t("یا ورود با روش‌های دیگر")}</span>
                </div>
                <div className="customer-auth-methods">
                  <Tooltip title={t("به‌زودی فعال می‌شود")}>
                    <Button
                      disabled
                      icon={<GoogleOutlined />}
                    >
                      {t("ورود با Google")}
                    </Button>
                  </Tooltip>
                </div>
              </div>
            </div>
          </Form>
        </>
      )}
    </CustomerAuthShell>
  );
}
